/**
 * One definition of "which org-chart tier means Location", shared by every
 * surface that builds a tier dropdown.
 *
 * THE BUG THIS EXISTS TO KILL. A company carries two different arrays:
 *
 *   `orgStructure`                 the tenant's NAMES for its tiers
 *   `additionalCompanyStructure`   the tiers themselves, with their values
 *
 * They are not the same length. On a real dev company `orgStructure` holds
 * five names while `additionalCompanyStructure` holds four levels, because the
 * name list is edited independently of the structure and keeps a name for a
 * level that no longer has any values.
 *
 * Every tier dropdown used to count `orgStructure`, so it offered "Org Level 5"
 * and put Location at 5. The backend counts `additionalCompanyStructure`
 * (`isLocationTier` in `common/utils/org-tier.util.ts`: `viewMode >= tierCount`),
 * so to it 4 already meant Location. Picking "Org Level 5" therefore sent
 * `viewMode: 4` and the backend answered with the LOCATION report — rows keyed
 * by `$location` over every group in the company. Two visible consequences:
 * the report silently showed locations under an org-level heading, and because
 * the location branch never consults `showCustomGroups`, custom groups appeared
 * in it with the "Custom Groups" box unticked.
 *
 * So the tier COUNT must come from the structure and only the NAMES from
 * `orgStructure`. A name with no level behind it is not a tier.
 */

/** Only the two fields that decide tiers; both are optional on the wire. */
export interface OrgTierCompany {
  orgStructure?: string[] | null;
  additionalCompanyStructure?: ReadonlyArray<{ level?: string | null }> | null;
}

/**
 * One name per REAL org level, in order.
 *
 * The count is `additionalCompanyStructure.length` — never `orgStructure`'s.
 * Names are taken from `orgStructure` by position, falling back to the level's
 * own `level` label and then to a positional one, so a structure that outgrows
 * the name list still gets a full set of labels.
 */
export function orgTierNames(company: OrgTierCompany | null | undefined): string[] {
  const structure = company?.additionalCompanyStructure ?? [];
  const names = company?.orgStructure ?? [];

  return structure.map((level, index) => names[index] ?? level?.level ?? `Org Level ${index + 1}`);
}

/**
 * The `viewMode` value that means Location: immediately past the last real tier.
 */
export function locationTierValue(tierCount: number): number {
  return tierCount;
}

/**
 * Whether a tier selection means the Location view.
 *
 * Deliberately identical to falcon-api's `isLocationTier`, including the
 * `tierCount > 0` guard — with no structure at all the backend takes the org
 * branch whatever `viewMode` says, so the frontend must not claim Location.
 */
export function isLocationTier(viewMode: number, tierCount: number): boolean {
  return tierCount > 0 && viewMode >= tierCount;
}

/**
 * The tier dropdown's options: one per real tier, then Location.
 *
 * Location is only offered when there is at least one tier, for the same
 * reason as the guard above — otherwise the option would be a value the
 * backend refuses to read as Location.
 */
export function orgTierOptions(
  company: OrgTierCompany | null | undefined,
): Array<{ label: string; value: number }> {
  const names = orgTierNames(company);
  const options = names.map((label, value) => ({ label, value }));

  if (names.length > 0) {
    options.push({ label: 'Location', value: locationTierValue(names.length) });
  }

  return options;
}

/**
 * The heat maps' `Organised by` options (MTFM-756): the two controls the
 * per-tier selector collapsed to. Each carries the `viewMode` value its branch
 * reads; the Location option is the value the backend already answers with the
 * location report. Empty when the company has no org structure at all, because
 * `Location` is then a value the backend refuses to read as Location (the guard
 * above).
 *
 * Where "Org chart" lands depends on the report:
 *
 *   'top'      the top tier (0). For a report whose rows carry expand carets
 *              (Change impacts, Activity burden): it opens collapsed at the top
 *              level and the user drills down through the carets, which fetch
 *              each subtree on demand -- the ticket's acceptance criterion.
 *   'deepest'  the company's deepest tier. For a report with no carets (the
 *              initiative Impacts heatmap), where the deepest read is the only
 *              one that shows the whole tree.
 *
 * Still a per-company computation for the same reason `orgTierOptions` is:
 * the deepest value and Location's value move when a tenant adds or removes a
 * level.
 */
export function organisedByOptions(
  company: OrgTierCompany | null | undefined,
  orgChartTier: 'top' | 'deepest' = 'deepest',
): Array<{ label: string; value: number }> {
  const names = orgTierNames(company);
  if (names.length === 0) return [];

  return [
    { label: 'Org chart', value: orgChartTier === 'top' ? 0 : names.length - 1 },
    { label: 'Location', value: locationTierValue(names.length) },
  ];
}

/**
 * The `viewMode` a report should actually run with, given the one remembered
 * in localStorage. Anything that is not Location means "Org chart".
 *
 * The stored value can be any tier: the old per-tier dropdown saved whichever
 * level a user last picked, and that key is still read. Running with it opened
 * Activity burden at that tier -- typically the deepest, every row expanded --
 * while the control claimed "Org chart" (MTFM-756).
 */
export function resolveOrganisedBy(
  storedViewMode: number,
  tierCount: number,
  orgChartTier: 'top' | 'deepest' = 'deepest',
): number {
  if (isLocationTier(storedViewMode, tierCount)) return locationTierValue(tierCount);
  return orgChartTier === 'top' || tierCount === 0 ? 0 : tierCount - 1;
}
